// @flow
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { get } from 'lodash';
import classNames from 'classnames';
import { type FieldProps } from 'formik';
import moment from 'moment';

import { getValidationStatus } from 'Components/frameworks/formik/inputsUtils';
import { ImageValidationStatus } from 'Components/frameworks/image-validation-status/ImageValidationStatus';
import { DatePicker } from './DatePicker.jsx';
import { Label } from './Label.jsx';

type Props = {
  dataTestId?: string,
  dateFormat?: string,
  defaultDuration?: number,
  isDateRange: boolean,
  isDisabled?: boolean,
  isRequired?: boolean,
  label?: string,
  maxDate?: Date,
  minDate?: Date,
  language?: string,
  placeholder?: string,
  disabledDays?: {
    after: Date,
    before: Date,
  },
  ...FieldProps,
};

const clickedElementIs = (element: EventTarget, elementType: string, dataTestId: string) =>
  element instanceof Element &&
  element.closest(elementType) &&
  // $FlowFixMe element.closest is not null or void
  element.closest(elementType).matches(`[data-testid="${dataTestId}"]`);

export const DatePickerInput = ({
  dataTestId,
  dateFormat = 'DD/MM/YYYY',
  defaultDuration = 1,
  field,
  form,
  isDateRange = false,
  isDisabled = false,
  isRequired = false,
  maxDate,
  minDate,
  label,
  language,
  placeholder,
  disabledDays,
}: Props) => {
  const [isActive, setIsActive] = useState(false);
  const datePickerReference = useRef(null);

  const { from, to } = get(form.values, field.name, {});
  const fromInputValue = from ? moment(from).format(dateFormat) : null;
  const toInputValue = to ? moment(to).format(dateFormat) : null;
  const inputValue: string = [fromInputValue, toInputValue].filter(value => !!value).join(' — ');
  const { isDefaultState, isErrored, isValidated } = getValidationStatus(field, form, isDisabled);

  // Open/Close effect
  useEffect(
    () => {
      const onClickOutside = (e: SyntheticEvent<>) => {
        const { current: datePickerElement } = datePickerReference;
        const clickedElement = e.target;
        const clickedElementIsPrevArrow = clickedElementIs(
          clickedElement,
          'button',
          'datepicker-arrow-prev',
        );
        const clickedElementIsNextArrow = clickedElementIs(
          clickedElement,
          'button',
          'datepicker-arrow-next',
        );
        if (
          datePickerElement &&
          !datePickerElement.contains(clickedElement) &&
          !clickedElementIsPrevArrow &&
          !clickedElementIsNextArrow
        ) {
          // 1. Set "to" date based on default duration if it's not defined
          if (isDateRange && !!from && !to) {
            form.setFieldValue(field.name, {
              from,
              to: moment(from)
                .add({ days: defaultDuration })
                .toDate(),
            });
          }
          // 2. Close DatePicker
          setIsActive(false);
        }
      };
      document.addEventListener('click', onClickOutside);
      document.addEventListener('focus', onClickOutside);
      return () => {
        document.removeEventListener('click', onClickOutside);
        document.removeEventListener('focus', onClickOutside);
      };
    },
    [from, to],
  );

  return (
    <Fragment>
      {!!label && (
        <Label htmlFor={field.name} isDisabled={isDisabled} isRequired={isRequired} label={label} />
      )}
      <div className="relative" ref={datePickerReference}>
        <div className="flex items-center">
          <input
            autoComplete="off"
            className={classNames(
              'w-full px-3 py-2 text-md sm:text-base text-gray-darker leading-normal border rounded-tiny outline-none transition-all',
              {
                'border-gray-medium': isDefaultState,
                'border-emerald  pr-6': isValidated,
                'border-red  pr-6': isErrored,
                'border-gray-lighter bg-gray-lighter cursor-not-allowed': isDisabled,
              },
            )}
            data-testid={dataTestId}
            disabled={isDisabled}
            {...field}
            onFocus={() => setIsActive(true)}
            placeholder={placeholder}
            type="text"
            value={inputValue}
          />
          {!isDisabled && (
            <ImageValidationStatus
              isErrored={isErrored}
              isValidated={isValidated}
              classNames="-m-5"
            />
          )}
        </div>
        {isActive && (
          <div className="absolute bg-white border-1 border-gray-medium left-1/2 min-w-full mt-2 px-3 py-2 rounded-tiny top-full -translate-x-50 z-2 fadeIn">
            <DatePicker
              field={field}
              form={form}
              isDateRange={isDateRange}
              locale={language}
              maxDate={maxDate}
              minDate={minDate}
              disabledDays={disabledDays}
            />
          </div>
        )}
      </div>
    </Fragment>
  );
};
